<template>
  <div class="video-config">
    <el-container direction="vertical">
      <AdminHeader @logout="handleLogout" />

      <el-container>
        <AdminSidebar />

        <el-main>
          <el-card shadow="always">
            <div class="page-title-row">
              <div class="page-title">门店视频管理</div>
              <div class="page-actions">
                <el-select
                  v-model="storeId"
                  placeholder="请选择门店"
                  filterable
                  style="width: 320px;"
                  @change="fetchVideos"
                >
                  <el-option
                    v-for="s in stores"
                    :key="String(s.id)"
                    :label="`${s.name || '门店'}（${s.id}）`"
                    :value="String(s.id)"
                  />
                </el-select>

                <el-upload
                  class="upload-btn"
                  :action="uploadUrl"
                  :data="{ storeId: storeId }"
                  :headers="uploadHeaders"
                  :show-file-list="false"
                  :disabled="!storeId"
                  accept="video/*"
                  :on-success="onUploadSuccess"
                  :on-error="onUploadError"
                >
                  <el-button type="primary" :disabled="!storeId">上传视频</el-button>
                </el-upload>
              </div>
            </div>

            <el-alert
              class="tips"
              type="info"
              :closable="false"
              title="提示：视频启用按门店生效；欢迎语开关不影响视频播放。大屏端仅播放已启用的视频。"
            />

            <el-table
              :data="videos"
              border
              style="width: 100%"
              height="520"
              v-loading="loading"
            >
              <el-table-column prop="id" label="ID" width="90" />
              <el-table-column prop="videoName" label="视频名" min-width="240" />
              <el-table-column prop="videoType" label="类型" width="160" />
              <el-table-column prop="isEnabled" label="是否启用" width="120">
                <template #default="{ row }">
                  <el-switch
                    v-model="row.isEnabled"
                    :disabled="!storeId"
                    @change="(v) => toggleEnabled(row, v)"
                  />
                </template>
              </el-table-column>
              <el-table-column label="操作" width="220">
                <template #default="{ row }">
                  <el-button type="text" @click="preview(row)">预览</el-button>
                  <el-button type="text" class="danger-text" @click="remove(row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-card>

          <el-dialog :visible.sync="previewVisible" width="70%" title="视频预览">
            <video
              v-if="previewUrl"
              :src="previewUrl"
              controls
              style="width: 100%; max-height: 70vh; background: #000;"
            />
          </el-dialog>
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config';
import { clearAuthInfo, getToken, getUserInfo } from '@/utils/auth';
import { parseApiResponse, handleApiError, snakeToCamel } from '@/utils/apiResponse';
import AdminHeader from '@/components/AdminHeader.vue';
import AdminSidebar from '@/components/AdminSidebar.vue';

export default {
  name: 'VideoConfigPanel',
  components: {
    AdminHeader,
    AdminSidebar
  },
  data() {
    return {
      stores: [],
      storeId: '',
      videos: [],
      loading: false,
      previewVisible: false,
      previewUrl: ''
    };
  },
  computed: {
    uploadUrl() {
      return config.getApiUrl(config.apiEndpoints.welcomeVideo.upload);
    },
    uploadHeaders() {
      /**
       * 说明：
       * - 你们在 src/main.js 里对 /api/store-welcome-video/ 已做了“管理端带 token、大屏不带 token”的拦截逻辑；
       * - 但 el-upload 不是 axios 请求，可能不会走 axios 拦截器，因此这里显式补 Authorization，避免上传/删除被 401。
       */
      const token = getToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    }
  },
  created() {
    const userInfo = getUserInfo();
    const stores = userInfo && userInfo.user && Array.isArray(userInfo.user.stores)
      ? userInfo.user.stores
      : [];

    // 兼容字段：id/name 或 store_id/store_name
    this.stores = stores
      .map(s => ({
        id: String(s.id || s.store_id),
        name: s.name || s.storeName || s.store_name
      }))
      .filter(s => s.id);

    // 默认选择第一个门店（也可以后续扩展为从 query 里带 storeId）
    if (this.stores.length > 0) {
      this.storeId = this.stores[0].id;
      this.fetchVideos();
    }
  },
  methods: {
    async fetchVideos() {
      if (!this.storeId) return;
      this.loading = true;
      try {
        // 管理端：不带 enabled=1，展示当前门店的全部视频（含未启用），便于管理
        const url = config.getApiUrl(config.apiEndpoints.welcomeVideo.store, this.storeId);
        const res = await axios.get(url);
        const parsed = parseApiResponse(res);

        let list = [];
        if (Array.isArray(parsed.data)) {
          list = parsed.data;
        } else if (parsed.data && Array.isArray(parsed.data.data)) {
          list = parsed.data.data;
        }

        this.videos = list.map(v => {
          const c = snakeToCamel(v);
          return {
            ...c,
            isEnabled: c.isEnabled === true || c.isEnabled === 1 || c.isEnabled === '1'
          };
        });
      } catch (e) {
        const err = handleApiError(e);
        this.$message.error(err.message || '获取视频列表失败');
        this.videos = [];
      } finally {
        this.loading = false;
      }
    },

    async toggleEnabled(row, enabled) {
      try {
        const url = config.getApiUrl(config.apiEndpoints.welcomeVideo.toggleEnabled, row.id);
        await axios.put(url, { is_enabled: enabled ? 1 : 0 });
        this.$message.success('更新成功');
      } catch (e) {
        const err = handleApiError(e);
        this.$message.error(err.message || '更新失败');
        // 回滚 UI 开关
        row.isEnabled = !enabled;
      }
    },

    preview(row) {
      this.previewUrl = config.getApiUrl(config.apiEndpoints.welcomeVideo.get, row.id);
      this.previewVisible = true;
    },

    async remove(row) {
      try {
        await this.$confirm('确定删除该视频吗？', '提示', { type: 'warning' });
        const url = config.getApiUrl(config.apiEndpoints.welcomeVideo.delete, row.id);
        await axios.delete(url);
        this.$message.success('删除成功');
        this.fetchVideos();
      } catch (e) {
        if (e === 'cancel') return;
        const err = handleApiError(e);
        this.$message.error(err.message || '删除失败');
      }
    },

    onUploadSuccess() {
      this.$message.success('上传成功（默认未启用，可在列表中打开开关）');
      this.fetchVideos();
    },
    onUploadError() {
      this.$message.error('上传失败');
    },

    handleLogout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        clearAuthInfo();
        this.$message.success('已退出登录');
        this.$router.push('/login');
      }).catch(() => {});
    }
  }
};
</script>

<style scoped>
.video-config {
  height: 100vh;
  overflow: hidden;
}

.el-container {
  height: 100%;
}

.header-container {
  background-color: #409eff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  flex-shrink: 0;
}

.el-header h1 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
}

.header-right {
  display: flex;
  align-items: center;
}

.el-aside {
  background-color: #f5f7fa;
  flex-shrink: 0;
}

.el-main {
  padding: 20px;
  overflow-y: auto;
  height: calc(100vh - 60px);
}

.page-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.page-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.page-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tips {
  margin-bottom: 12px;
}

.danger-text {
  color: #f56c6c;
}
</style>

